import { Trash2, Pencil, Eye, MoreVertical, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TableRow, TableCell } from '@/components/ui/table';
import { expenseProjectName } from '@jobos/shared';
import type { ExpenseWithDetails, ExpensePaymentMethod } from '@jobos/shared';

interface ExpenseRowProps {
  expense: ExpenseWithDetails;
  hasProjects: boolean;
  onEdit: (expense: ExpenseWithDetails) => void;
  onDelete: (id: string) => void;
  onViewReceipt: (expense: ExpenseWithDetails) => void;
}

const PAYMENT_METHOD_LABELS: Record<ExpensePaymentMethod, string> = {
  check: 'Check',
  credit_card: 'Credit Card',
  cash: 'Cash',
};

export function formatPaymentMethod(method: ExpensePaymentMethod | null): string {
  return method ? PAYMENT_METHOD_LABELS[method] : '—';
}

function formatDate(date: string): string {
  return new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * One expense.
 *
 * Nine columns is a spreadsheet, and a spreadsheet on a phone is a thing you
 * drag sideways. Below `sm` the row stops being a table row and becomes a
 * stacked line — store and amount on top, the rest folded into a secondary line
 * under it — and the two hover-only icon buttons become one menu, because a
 * touch screen never hovers and 28px is not a target.
 */
export function ExpenseRow({ expense, hasProjects, onEdit, onDelete, onViewReceipt }: ExpenseRowProps) {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(expense.amount);
  const isRefund = expense.amount < 0;
  const hasReceipt = expense.fileId != null;

  return (
    <TableRow className="flex flex-wrap items-center gap-x-2 sm:table-row">
      {/* The stacked mobile line. It carries everything the folded columns
          would have said, so nothing below is load-bearing on a phone. */}
      <TableCell className="min-w-0 flex-1 py-2 sm:hidden">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{expense.storeName}</span>
          <span className={`whitespace-nowrap text-sm font-semibold ${isRefund ? 'text-green-600' : ''}`}>
            {formattedAmount}
          </span>
        </div>
        <div className="mt-0.5 truncate text-xs text-gray-500">
          {formatDate(expense.expenseDate)} · {expense.category.name} ·{' '}
          {formatPaymentMethod(expense.paymentMethod)}
          {expense.reimburse ? ' · Reimburse' : ''}
        </div>
        {(expense.description || hasReceipt) && (
          <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
            {hasReceipt && (
              <button
                type="button"
                onClick={() => onViewReceipt(expense)}
                className="inline-flex items-center gap-1 text-blue-600"
              >
                <Paperclip className="h-3 w-3" /> Receipt
              </button>
            )}
            {expense.description && <span className="min-w-0 truncate">{expense.description}</span>}
          </div>
        )}
      </TableCell>

      <TableCell className="hidden text-sm text-gray-600 sm:table-cell">
        {expense.paidDate ? formatDate(expense.paidDate) : <span className="text-gray-400">—</span>}
      </TableCell>
      <TableCell className="hidden text-sm text-gray-600 sm:table-cell">
        {formatDate(expense.expenseDate)}
      </TableCell>
      <TableCell className="hidden text-sm font-medium sm:table-cell">{expense.storeName}</TableCell>
      <TableCell className="hidden sm:table-cell">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
          style={{
            backgroundColor: `${expense.category.color}20`,
            color: expense.category.color,
          }}
        >
          {expense.category.name}
        </span>
      </TableCell>
      <TableCell className="hidden text-sm text-gray-600 sm:table-cell">{expense.description}</TableCell>
      {hasProjects && (
        <TableCell className="hidden text-sm text-gray-600 sm:table-cell">
          {expenseProjectName(expense) ?? '—'}
        </TableCell>
      )}
      <TableCell className="hidden text-sm text-gray-600 sm:table-cell">
        {formatPaymentMethod(expense.paymentMethod)}
      </TableCell>
      <TableCell className="hidden text-sm sm:table-cell">
        {expense.reimburse ? (
          <span className="font-medium text-green-600">Yes</span>
        ) : (
          <span className="text-gray-400">No</span>
        )}
      </TableCell>
      <TableCell
        className={`hidden text-right text-sm font-medium sm:table-cell ${isRefund ? 'text-green-600' : ''}`}
      >
        {formattedAmount}
      </TableCell>
      <TableCell className="hidden text-right text-sm sm:table-cell">
        {hasReceipt ? (
          <button
            type="button"
            onClick={() => onViewReceipt(expense)}
            className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
          >
            View <Eye className="h-3 w-3" />
          </button>
        ) : (
          <span className="text-gray-400">None</span>
        )}
      </TableCell>

      <TableCell className="w-12 shrink-0 py-2 text-right">
        <div className="hidden items-center gap-1 sm:flex">
          <Button variant="ghost" size="sm" onClick={() => onEdit(expense)} className="h-7 w-7 p-0">
            <Pencil className="h-3.5 w-3.5 text-gray-400" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(expense.id)} className="h-7 w-7 p-0">
            <Trash2 className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
          </Button>
        </div>
        <div className="sm:hidden">
          <ExpenseActionsMenu
            expense={expense}
            onEdit={onEdit}
            onDelete={onDelete}
            onViewReceipt={onViewReceipt}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

function ExpenseActionsMenu({
  expense,
  onEdit,
  onDelete,
  onViewReceipt,
}: Omit<ExpenseRowProps, 'hasProjects'>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10"
          aria-label={`Actions for ${expense.storeName}`}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {expense.fileId != null && (
          <DropdownMenuItem onClick={() => onViewReceipt(expense)}>View receipt</DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onEdit(expense)}>Edit</DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onDelete(expense.id)}
          className="text-red-600 hover:bg-red-50 focus:bg-red-50"
        >
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
